import {useQuery} from "@apollo/client";

import {CheckFormResult, GQL_CHECK_FORM} from "@/lib/queries/checks.ts";
import {MonitoringServiceType, Service} from "@/lib/types/checks.ts";
import {filterAllowedMonitoringServersForCheckType} from "@/pages/checks/components/form/utils";

import {useNetworkRunTest} from "./useNetworkRunTest.ts";

/**
 * Run Test for a NETWORK check on the Analysis page. Fetches the (check-independent)
 * form scaffolding for the run-test URL + a valid test-location server, and runs
 * against the check's saved config — the backend merges from the DB when given the
 * check `id`, so the payload is minimal. Traceroute is forced on for analysis runs.
 */
export const useNetworkAnalysisRunTest = (service: Service | undefined, enabled: boolean) => {
  const {data} = useQuery<CheckFormResult>(GQL_CHECK_FORM, {skip: !enabled});
  const {state, result, error, run} = useNetworkRunTest({
    runTestURL: data?.legacyURL.runTestURL ?? "",
  });

  const serviceDef = data?.serviceDefinitions.find((d) => d.service_type === MonitoringServiceType.NETWORK);
  const testLocationId =
    data && serviceDef
      ? filterAllowedMonitoringServersForCheckType({
          monitoringGroupsArchMapper: data.monitoringGroupsArchMapper,
          serviceDef,
          monitoringServiceType: MonitoringServiceType.NETWORK,
          monitoringServers: serviceDef.can_run_test_on_public
            ? data.runTestMonitoringServers
            : data.runTestMonitoringServers.filter((s) => s.is_test || s.is_private),
        })[0]?.id
      : undefined;

  const handleRun = () => {
    if (!service || !testLocationId) return;
    run({
      id: service.id,
      monitoring_service_type: MonitoringServiceType.NETWORK,
      test_location: testLocationId,
      // Analysis runs always capture a traceroute regardless of the saved config.
      network_capture_traceroute: true,
    });
  };

  return {
    state,
    result,
    error,
    handleRun,
    ready: !!testLocationId,
    title: "Execution Details",
  };
};
